import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { triggerZapierEvent } from '../../utils/zapier';

const ClientsSection = ({ trainerId, onClientSelect, onRefresh }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');
  const [successToast, setSuccessToast] = useState('');

  const initialFormState = {
    owner_name: '',
    dog_name: '',
    email: '',
    phone: '',
    notes: '',
    messaging_enabled: false // ENSURE DEFAULT IS FALSE
  };

  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    if (trainerId) fetchClients();
  }, [trainerId]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('training_clients')
        .select(`
          *,
          training_plans (
            id,
            plan_name,
            start_date
          )
        `)
        .eq('trainer_id', trainerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setClients(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleShowToast = (msg) => {
    setSuccessToast(msg);
    setTimeout(() => setSuccessToast(''), 3000);
  };

  const handleDelete = async (client) => {
    const confirmMsg = `Permanently delete ${client.dog_name} and ${client.owner_name}?\n\nThis will instantly remove all their training plans, tasks, and history. This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    setDeletingId(client.id);
    setError('');

    try {
      const { data, error: rpcError } = await supabase.rpc('delete_client_v2', { target_client_id: client.id });
      if (rpcError) throw rpcError;
      
      if (data?.success) {
        handleShowToast(`Deleted ${client.dog_name}'s profile.`);
        fetchClients();
        if (onRefresh) onRefresh();
      } else {
        throw new Error(data?.message || 'Deletion failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Delete failed: ' + err.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    setError('');

    try {
      const payload = {
        trainer_id: trainerId,
        owner_name: formData.owner_name.trim(),
        dog_name: formData.dog_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        notes: formData.notes.trim(),
        messaging_enabled: formData.messaging_enabled
      };

      if (editingClient) {
        const { error: updateError } = await supabase
          .from('training_clients')
          .update(payload)
          .eq('id', editingClient.id);
        
        if (updateError) throw updateError;
        handleShowToast('Client updated!');
      } else {
        const { error: insertError } = await supabase
          .from('training_clients')
          .insert([payload]);
        
        if (insertError) throw insertError;
        
        triggerZapierEvent(trainerId, 'client_created', {
          owner_name: payload.owner_name,
          dog_name: payload.dog_name,
          email: payload.email,
          phone: payload.phone
        });
        
        handleShowToast('Client added!');
      }

      setShowForm(false);
      setEditingClient(null);
      setFormData(initialFormState);
      fetchClients();
      if (onRefresh) onRefresh();
    } catch (err) {
      setError(err.message || 'Failed to save client');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredClients = clients.filter(c => 
    c.owner_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.dog_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="p-8 animate-pulse grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">{[1,2,3,4,5,6].map(i => <div key={i} className="h-44 bg-white rounded-[2rem] border" />)}</div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-gray-900">Client Directory</h2>
          <p className="text-sm text-gray-500 font-medium">Manage your pack and their owners</p>
        </div>
        <button 
          onClick={() => { setEditingClient(null); setFormData(initialFormState); setShowForm(true); }}
          className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-4 rounded-2xl font-black transition-all flex items-center gap-2 shadow-xl shadow-indigo-100 active:scale-95"
        >
          <SafeIcon name="Plus" className="w-5 h-5" /> Add New Client
        </button>
      </div>

      <AnimatePresence>
        {successToast && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold shadow-2xl flex items-center gap-2 fixed top-24 right-8 z-[100]"
          >
            <SafeIcon name="CheckCircle" /> {successToast}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="relative">
        <SafeIcon name="Search" className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input 
          type="text"
          placeholder="Search by owner or dog name..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="w-full pl-14 pr-6 py-5 bg-white border-2 border-gray-100 rounded-[2rem] font-bold text-gray-700 shadow-sm outline-none focus:border-indigo-500 transition-all"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredClients.map(client => (
          <div key={client.id} className={`bg-white p-6 rounded-[2.5rem] border transition-all group ${deletingId === client.id ? 'opacity-50 grayscale scale-95' : 'border-gray-100 shadow-sm hover:shadow-xl hover:border-indigo-100'}`}>
            <div className="flex justify-between items-start mb-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center font-black text-2xl shadow-inner">
                  {client.dog_name.charAt(0)}
                </div>
                <div>
                  <h3 className="font-black text-xl text-gray-900 leading-tight">{client.dog_name}</h3>
                  <p className="text-xs text-gray-400 font-black uppercase tracking-widest">{client.owner_name}</p>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button 
                  onClick={() => {
                    setEditingClient(client);
                    setFormData({
                      owner_name: client.owner_name,
                      dog_name: client.dog_name,
                      email: client.email || '',
                      phone: client.phone || '',
                      notes: client.notes || '',
                      messaging_enabled: client.messaging_enabled || false
                    });
                    setShowForm(true);
                  }}
                  className="p-2.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
                >
                  <SafeIcon name="Edit" className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleDelete(client)}
                  disabled={deletingId === client.id}
                  className="p-2.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                >
                  {deletingId === client.id ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <SafeIcon name="Trash2" className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-3 mb-8">
              {client.email && (
                <div className="flex items-center gap-3 text-sm text-gray-500 font-medium">
                  <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400"><SafeIcon name="MessageSquare" className="w-4 h-4" /></div>
                  <span className="truncate">{client.email}</span>
                </div>
              )}
              {client.phone && (
                <div className="flex items-center gap-3 text-sm text-gray-500 font-medium">
                  <div className="w-8 h-8 bg-gray-50 rounded-lg flex items-center justify-center text-gray-400"><SafeIcon name="MessageCircle" className="w-4 h-4" /></div>
                  <span>{client.phone}</span>
                </div>
              )}
            </div>

            <div className="pt-5 border-t border-gray-50 flex justify-between items-center">
              <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">Curricula</span>
                <span className="text-sm font-black text-gray-900">
                  {client.training_plans?.length || 0} Active {client.training_plans?.length === 1 ? 'Plan' : 'Plans'}
                </span>
              </div>
              <button 
                onClick={() => onClientSelect(client)}
                className="text-xs font-black text-indigo-600 bg-indigo-50 hover:bg-indigo-600 hover:text-white px-5 py-3 rounded-xl transition-all active:scale-95"
              >
                Manage Plans
              </button>
            </div>
          </div>
        ))}

        {filteredClients.length === 0 && (
          <div className="col-span-full py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-gray-100">
            <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <SafeIcon name="Users" className="w-10 h-10 text-gray-300" />
            </div>
            <h3 className="text-2xl font-black text-gray-400">No clients found</h3>
            <p className="text-gray-400 font-medium mt-2">Add your first client to start building training plans</p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showForm && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-md flex items-center justify-center p-4 z-[9999]" onClick={() => setShowForm(false)}>
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-white rounded-[3rem] shadow-2xl w-full max-w-xl overflow-hidden flex flex-col max-h-[90vh]"
              onClick={e => e.stopPropagation()}
            >
              <div className="p-8 border-b flex justify-between items-center bg-gray-50/50">
                <div>
                  <h3 className="text-2xl font-black text-gray-900">{editingClient ? 'Edit Client' : 'New Client'}</h3>
                  <p className="text-sm text-gray-500 font-medium">Capture the essential details</p>
                </div>
                <button onClick={() => setShowForm(false)} className="p-3 hover:bg-gray-200 rounded-2xl transition-colors">
                  <SafeIcon name="X" className="w-6 h-6 text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-8 overflow-y-auto flex-1 space-y-6">
                {error && (
                  <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-sm font-bold flex items-center gap-3 border border-red-100">
                    <SafeIcon name="AlertTriangle" className="w-5 h-5" />
                    {error}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Dog's Name</label>
                    <input 
                      required 
                      value={formData.dog_name}
                      onChange={e => setFormData({...formData, dog_name: e.target.value})}
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all text-gray-900"
                      placeholder="e.g. Buddy"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Owner's Name</label>
                    <input 
                      required 
                      value={formData.owner_name}
                      onChange={e => setFormData({...formData, owner_name: e.target.value})}
                      className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all text-gray-900"
                      placeholder="e.g. Jane Doe"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Email Address</label>
                  <input 
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all text-gray-900"
                    placeholder="email@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                  <input 
                    type="tel"
                    value={formData.phone}
                    onChange={e => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-bold transition-all text-gray-900"
                    placeholder="(555) 000-0000"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Internal Coaching Notes</label>
                  <textarea 
                    value={formData.notes}
                    onChange={e => setFormData({...formData, notes: e.target.value})}
                    rows={4}
                    placeholder="Private notes about dog behavior, issues, or goals..."
                    className="w-full px-5 py-4 bg-gray-50 border-2 border-transparent rounded-2xl focus:border-indigo-500 focus:bg-white outline-none font-medium transition-all resize-none text-gray-700"
                  />
                </div>

                <div className="bg-indigo-50 p-6 rounded-[2rem] border border-indigo-100 shadow-inner">
                  <label className="flex items-center gap-4 cursor-pointer group">
                    <div className="relative">
                      <input 
                        type="checkbox" 
                        checked={formData.messaging_enabled}
                        onChange={e => setFormData({...formData, messaging_enabled: e.target.checked})}
                        className="peer sr-only"
                      />
                      <div className="w-12 h-6 bg-gray-300 rounded-full peer-checked:bg-indigo-600 transition-colors" />
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-6" />
                    </div>
                    <div>
                      <span className="block text-base font-black text-indigo-900">Enable Portal Messaging</span>
                      <span className="block text-xs text-indigo-600 font-medium">Allows owner to chat with you directly in their portal</span>
                    </div>
                  </label>
                </div>
              </form>

              <div className="p-8 border-t bg-gray-50 flex gap-4">
                <button 
                  onClick={handleSubmit} 
                  disabled={isSubmitting}
                  className="flex-[2] bg-indigo-600 text-white py-5 rounded-2xl font-black shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  ) : 'Confirm & Save Client'}
                </button>
                <button 
                  onClick={() => setShowForm(false)}
                  className="flex-1 bg-white border-2 border-gray-100 py-5 rounded-2xl font-black text-gray-500 hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ClientsSection;